/**
 * zigbee.js — Z-Stack MT protocol driver for the Web Serial API.
 *
 * Usage:
 *   import { ZStackController } from './zigbee.js';
 *   const zb = new ZStackController();
 *   zb.addEventListener('log',           e => console.log(e.detail));
 *   zb.addEventListener('device-joined', e => console.log(e.detail));
 *   zb.addEventListener('device-left',   e => console.log(e.detail));
 *   zb.addEventListener('message',       e => console.log(e.detail));
 *   zb.addEventListener('disconnected',  () => { ... });
 *   await zb.open(115200);
 *   await zb.permitJoin(true, 300);
 */

// ═══════════════════════════════════════════════════════════════════════════════
//  Z-Stack MT protocol constants (internal)
// ═══════════════════════════════════════════════════════════════════════════════

const SOF = 0xFE;

const MT_TYPE = { POLL: 0, SREQ: 1, AREQ: 2, SRSP: 3 };
const MT_SUB  = { SYS: 1, AF: 4, ZDO: 5, UTIL: 7 };

const cmd0 = (type, sub) => (type << 5) | sub;

const CMD = {
  // SYS
  SYS_PING:        { c0: cmd0(MT_TYPE.SREQ, MT_SUB.SYS),  c1: 0x01 },
  SYS_VERSION:     { c0: cmd0(MT_TYPE.SREQ, MT_SUB.SYS),  c1: 0x02 },
  SYS_RESET_REQ:   { c0: cmd0(MT_TYPE.AREQ, MT_SUB.SYS),  c1: 0x09 },
  SYS_RESET_IND:   { c0: cmd0(MT_TYPE.AREQ, MT_SUB.SYS),  c1: 0x80 },
  // ZDO
  ZDO_STARTUP:     { c0: cmd0(MT_TYPE.SREQ, MT_SUB.ZDO),  c1: 0x40 },
  ZDO_PERMIT_JOIN: { c0: cmd0(MT_TYPE.SREQ, MT_SUB.ZDO),  c1: 0x36 },
  ZDO_STATE_IND:   { c0: cmd0(MT_TYPE.AREQ, MT_SUB.ZDO),  c1: 0xC0 },
  ZDO_TC_DEV_IND:  { c0: cmd0(MT_TYPE.AREQ, MT_SUB.ZDO),  c1: 0xCA },
  ZDO_ANNCE_IND:   { c0: cmd0(MT_TYPE.AREQ, MT_SUB.ZDO),  c1: 0xFF },
  ZDO_LEAVE_IND:   { c0: cmd0(MT_TYPE.AREQ, MT_SUB.ZDO),  c1: 0xC9 },
  ZDO_PJ_IND:      { c0: cmd0(MT_TYPE.AREQ, MT_SUB.ZDO),  c1: 0xCB },
  // AF
  AF_REGISTER:     { c0: cmd0(MT_TYPE.SREQ, MT_SUB.AF),   c1: 0x00 },
  AF_DATA_REQUEST: { c0: cmd0(MT_TYPE.SREQ, MT_SUB.AF),   c1: 0x01 },
  AF_INCOMING_MSG: { c0: cmd0(MT_TYPE.AREQ, MT_SUB.AF),   c1: 0x81 },
  // UTIL
  UTIL_GET_DEVICE:         { c0: cmd0(MT_TYPE.SREQ, MT_SUB.UTIL), c1: 0x00 },
  UTIL_ASSOC_COUNT:        { c0: cmd0(MT_TYPE.SREQ, MT_SUB.UTIL), c1: 0x48 },
  UTIL_ASSOC_FIND_DEVICE:  { c0: cmd0(MT_TYPE.SREQ, MT_SUB.UTIL), c1: 0x41 },
  UTIL_ASSOC_GET_WITH_ADDRESS: { c0: cmd0(MT_TYPE.SREQ, MT_SUB.UTIL), c1: 0x42 },
};

const ZDO_STATE_NAMES = {
  0: 'DEV_HOLD',        1: 'DEV_INIT',          2: 'NWK_DISC',
  3: 'NWK_JOINING',     4: 'NWK_REJOIN',         5: 'END_DEVICE_UNAUTH',
  6: 'END_DEVICE',      7: 'ROUTER',             8: 'COORD_STARTING',
  9: 'ZB_COORD (ready)',
};

const RESET_REASONS = { 0: 'Power-up', 1: 'External', 2: 'Watch-dog' };

// ═══════════════════════════════════════════════════════════════════════════════
//  Frame helpers
// ═══════════════════════════════════════════════════════════════════════════════

const fcs     = (bytes) => bytes.reduce((a, b) => a ^ b, 0);
const delay   = (ms)    => new Promise(r => setTimeout(r, ms));
const le16    = (buf, offset) => buf[offset] | (buf[offset + 1] << 8);

function buildFrame(cmd0b, cmd1, data = []) {
  const chk = fcs([data.length, cmd0b, cmd1, ...data]);
  return new Uint8Array([SOF, data.length, cmd0b, cmd1, ...data, chk]);
}

function ieeeToStr(buf, offset) {
  return Array.from(buf.slice(offset, offset + 8))
    .reverse()
    .map(b => b.toString(16).padStart(2, '0'))
    .join(':');
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MT frame parser (state machine)
// ═══════════════════════════════════════════════════════════════════════════════

class ZStackParser {
  constructor(onFrame) {
    this.onFrame = onFrame;
    this.reset();
  }

  reset() {
    this.state  = 'SOF';
    this.rxLen  = 0; this.rxCmd0 = 0; this.rxCmd1 = 0;
    this.rxData = []; this.rxBuf = [];
  }

  feed(chunk) {
    for (const byte of chunk) this._byte(byte);
  }

  _byte(b) {
    switch (this.state) {
      case 'SOF':
        if (b === SOF) { this.rxBuf = []; this.state = 'LEN'; }
        break;
      case 'LEN':
        this.rxLen = b; this.rxBuf = [b]; this.state = 'CMD0';
        break;
      case 'CMD0':
        this.rxCmd0 = b; this.rxBuf.push(b); this.state = 'CMD1';
        break;
      case 'CMD1':
        this.rxCmd1 = b; this.rxBuf.push(b);
        this.rxData = [];
        this.state  = this.rxLen > 0 ? 'DATA' : 'FCS';
        break;
      case 'DATA':
        this.rxData.push(b); this.rxBuf.push(b);
        if (this.rxData.length === this.rxLen) this.state = 'FCS';
        break;
      case 'FCS': {
        const expected = fcs(this.rxBuf);
        if (b === expected) {
          this.onFrame(this.rxCmd0, this.rxCmd1, this.rxData);
        } else {
          // Emit via a callback the controller wires up
          this._onFcsError?.(b, expected);
        }
        this.state = 'SOF';
        break;
      }
      // no default
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  ZStackController — exported class
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Events emitted (via CustomEvent.detail):
 *
 *  'log'           { level, message, detail? }
 *  'device-joined' { ieee, nwkAddr, capabilities?, via }
 *  'device-left'   { ieee }
 *  'message'       { srcAddr, srcEP, dstEP, groupID, clusterID, lqi, msgLen, payload }
 *  'state-change'  { state, name }
 *  'disconnected'  (no detail)
 */
export class ZStackController extends EventTarget {

  constructor() {
    super();
    this._port          = null;
    this._writer        = null;
    this._readAbort     = null;
    this._activeReader  = null;   // held so close() can cancel the pending read
    this._pendingSrsp   = null;
    this._areqWaiters   = [];
    this._parser        = new ZStackParser(this._handleFrame.bind(this));
    this._parser._onFcsError = (got, exp) =>
      this._log('warn', `FCS mismatch got 0x${got.toString(16)} expected 0x${exp.toString(16)}`);

    // Permit-join state
    this._pjTotal     = 0;
    this._pjRemaining = 0;
    this._pjTimerId   = null;

    // Track devices we have already sent Poll Control config to, to avoid
    // re-sending on every re-announcement (which congests the coordinator MAC layer).
    this._configuredDevices = new Set();

    // Track devices that have joined the network; messages from unknown devices
    // are ignored until they complete the join handshake.
    this._joinedDevices = new Set();

    // Known devices persisted to localStorage (ieee → nwkAddr numeric).
    // Pre-populated on construction so previously paired devices are accepted
    // immediately without waiting for a re-announcement.
    this._knownDevices = new Map(); // ieee → nwkAddr (numeric)
    this._loadKnownDevices();

    // Mutex: serialise all _sendSreq calls so concurrent async paths
    // (e.g. ZCL default-response + _configureDevice) never stomp on the
    // single _pendingSrsp slot.
    this._sreqTail = Promise.resolve();
  }
  // ── Known-device persistence ───────────────────────────────────────────────

  _loadKnownDevices() {
    try {
      const saved = JSON.parse(localStorage.getItem('zigbee-known-devices') || '[]');
      for (const { ieee, nwkAddr } of saved) {
        this._knownDevices.set(ieee, nwkAddr);
        this._joinedDevices.add(nwkAddr);
      }
      if (saved.length) this._log('info', `Loaded ${saved.length} known device(s) from storage`);
    } catch (_) {}
  }

  _saveKnownDevices() {
    try {
      const arr = Array.from(this._knownDevices.entries()).map(([ieee, nwkAddr]) => ({ ieee, nwkAddr }));
      localStorage.setItem('zigbee-known-devices', JSON.stringify(arr));
    } catch (_) {}
  }

  _registerDevice(ieee, nwkAddrNum) {
    // Update NWK address in case it changed after a coordinator reset
    const prev = this._knownDevices.get(ieee);
    if (prev !== undefined && prev !== nwkAddrNum) {
      this._joinedDevices.delete(prev);
      this._log('info', `Device ${ieee} NWK updated: 0x${prev.toString(16)} → 0x${nwkAddrNum.toString(16)}`);
    }
    this._knownDevices.set(ieee, nwkAddrNum);
    this._joinedDevices.add(nwkAddrNum);
    this._saveKnownDevices();
  }
  // ── Public API ─────────────────────────────────────────────────────────────
  async open(baudRate) {
    // If already open, close first so the user can re-connect
    if (this._port) {
      this._log('warn', 'Port already open — closing before re-opening…');
      await this.close();
    }
    // Web Serial API feature detection
    if (!('serial' in navigator) || typeof navigator.serial.requestPort !== 'function') {
      this._log('error', 'Web Serial API is not supported in this browser.');
      throw new Error('Web Serial API is not supported in this browser. Please use Chrome or Edge.');
    }
    this._port = await navigator.serial.requestPort();
    await this._port.open({ baudRate, bufferSize: 32 });
    this._writer     = this._port.writable.getWriter();
    this._readAbort  = new AbortController();
    this._readLoop(this._readAbort.signal);
    this._log('ok', `Serial port opened at ${baudRate} bps`);
    this._emit('port-opened');

    await this._bootloaderSkip();
    return this._initCoordinator();
  }

  /**
   * Read all devices currently associated with the coordinator from Z-Stack,
   * reconcile with _joinedDevices/_knownDevices, and emit 'network-devices'
   * with a list of { ieee, nwkAddr } objects.
   */
  async readNetworkDevices() {
    // 1. Get count of associated devices
    let countRsp;
    try {
      countRsp = await this._sendSreq(
        CMD.UTIL_ASSOC_COUNT.c0, CMD.UTIL_ASSOC_COUNT.c1,
        // startRelation=0x01 (CHILD_RFD), endRelation=0x04 (CHILD_FFD_RXIDLE)
        // counts all end-device and router children of the coordinator
        [0x01, 0x04], 4000,
      );
    } catch (e) {
      this._log('warn', `UTIL_ASSOC_COUNT failed: ${e.message}`);
      return [];
    }
    const count = le16(countRsp.data, 0);
    this._log('info', `Network has ${count} associated device(s)`);
    if (count === 0) {
      this._emit('network-devices', { devices: [] });
      return [];
    }

    const devices = [];
    for (let i = 0; i < count; i++) {
      try {
        // UTIL_ASSOC_FIND_DEVICE: returns the IEEE address of the i-th child
        const findRsp = await this._sendSreq(
          CMD.UTIL_ASSOC_FIND_DEVICE.c0, CMD.UTIL_ASSOC_FIND_DEVICE.c1,
          [i], 4000,
        );
        const ieee = ieeeToStr(findRsp.data, 0);

        // Use the cached NWK address from localStorage if available.
        // UTIL_ASSOC_GET_WITH_ADDRESS is unreliable across firmware versions and causes
        // SRSP mismatches when AF messages arrive concurrently. The NWK address will be
        // refreshed automatically on the next device announcement.
        const cachedNwk = this._knownDevices.get(ieee) ?? 0xFFFE;
        const nwkAddr   = '0x' + cachedNwk.toString(16).toUpperCase().padStart(4, '0');
        this._log('info', `  [${i}] IEEE: ${ieee}  NWK: ${nwkAddr}${cachedNwk === 0xFFFE ? ' (pending re-announce)' : ''}`);
        // Register so messages from this device are accepted immediately
        if (cachedNwk !== 0xFFFE) this._registerDevice(ieee, cachedNwk);
        else this._joinedDevices.add(cachedNwk); // placeholder; updated on re-announce
        devices.push({ ieee, nwkAddr });
      } catch (e) {
        this._log('warn', `Could not read device ${i}: ${e.message}`);
      }
    }

    this._emit('network-devices', { devices });
    return devices;
  }

  /** Close the serial port gracefully. */
  async close() {
    this._stopPjCountdown();
    try { await this._sendPermitJoinCmd(0); } catch (_) {}
    await this._serialClose();
    this._emit('disconnected');
    this._log('info', 'Disconnected');
  }

  /** Send a soft MT reset then re-initialise. */
  async reset() {
    this._log('warn', 'Sending soft reset…');
    this._stopPjCountdown();
    await this._sendFrame(CMD.SYS_RESET_REQ.c0, CMD.SYS_RESET_REQ.c1, [0x01]);
    await this._waitForAreq(CMD.SYS_RESET_IND.c0, CMD.SYS_RESET_IND.c1, 5000).catch(() => {});
    await delay(500);
    this._parser.reset();
    return this._initCoordinator();
  }

  /**
   * Enable or disable permit join.
   * Z-Stack max per call is 254 s — this helper re-schedules automatically
   * for durations up to 600 s.
   */
  async permitJoin(enable, totalSeconds = 300) {
    if (enable) {
      this._pjTotal     = totalSeconds;
      this._pjRemaining = totalSeconds;
      await this._sendPermitJoinCmd(Math.min(totalSeconds, 254));
      this._startPjCountdown();
    } else {
      this._stopPjCountdown();
      await this._sendPermitJoinCmd(0);
      this._log('info', 'Permit join closed');
    }
  }

  // ── Serial transport ────────────────────────────────────────────────────────

  async _serialClose() {
    if (this._readAbort) { this._readAbort.abort(); this._readAbort = null; }
    // Cancel the active reader so its pending read() call unblocks immediately,
    // making the read loop release the lock before we close the port.
    if (this._activeReader) {
      try { await this._activeReader.cancel(); } catch (_) {}
      this._activeReader = null;
    }
    if (this._writer) { try { this._writer.releaseLock(); } catch (_) {} this._writer = null; }
    if (this._port)   { try { await this._port.close(); }  catch (_) {} this._port   = null; }
  }

  async _readLoop(signal) {
    while (this._port?.readable && !signal.aborted) {
      let reader;
      try {
        reader = this._port.readable.getReader();
        this._activeReader = reader;
        while (true) {
          const { value, done } = await reader.read();
          if (done || signal.aborted) break;
          this._parser.feed(value);
        }
      } catch (e) {
        if (!signal.aborted) this._log('error', `Serial read error: ${e.message}`);
      } finally {
        this._activeReader = null;
        if (reader) reader.releaseLock();
      }
      if (signal.aborted) break;
    }
    if (!signal.aborted) {
      this._log('error', 'Serial port closed unexpectedly');
      this._emit('disconnected');
    }
  }

  async _sendFrame(cmd0b, cmd1, data = []) {
    if (!this._writer) throw new Error('Port not open');
    await this._writer.write(buildFrame(cmd0b, cmd1, data));
  }

  async _sendRaw(bytes) {
    if (!this._writer) throw new Error('Port not open');
    await this._writer.write(bytes);
  }

  /** Send SREQ, await matching SRSP (default 4 s timeout). */
  _sendSreq(cmd0b, cmd1, data = [], timeoutMs = 4000) {
    // Chain onto the tail of any in-flight SREQ so calls are strictly sequential.
    const queued = this._sreqTail.then(() => this._sendSreqNow(cmd0b, cmd1, data, timeoutMs));
    // The tail must never reject (so the chain stays alive even after errors).
    this._sreqTail = queued.catch(() => {});
    return queued;
  }

  _sendSreqNow(cmd0b, cmd1, data = [], timeoutMs = 4000) {
    return new Promise((resolve, reject) => {
      const srspCmd0 = (cmd0b & 0x1F) | (MT_TYPE.SRSP << 5);
      const timer = setTimeout(() => {
        if (this._pendingSrsp?.resolve === resolve) this._pendingSrsp = null;
        reject(new Error(`SREQ timeout CMD0=0x${cmd0b.toString(16)} CMD1=0x${cmd1.toString(16)}`));
      }, timeoutMs);
      this._pendingSrsp = {
        cmd0: srspCmd0, cmd1,
        resolve: (r) => { clearTimeout(timer); this._pendingSrsp = null; resolve(r); },
        reject:  (e) => { clearTimeout(timer); this._pendingSrsp = null; reject(e); },
      };
      this._sendFrame(cmd0b, cmd1, data).catch(reject);
    });
  }

  /** Wait for a specific AREQ (one-shot). */
  _waitForAreq(cmd0b, cmd1, timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
      let waiter;
      const timer = timeoutMs ? setTimeout(() => {
        const i = this._areqWaiters.indexOf(waiter);
        if (i !== -1) this._areqWaiters.splice(i, 1);
        reject(new Error(`AREQ timeout CMD0=0x${cmd0b.toString(16)} CMD1=0x${cmd1.toString(16)}`));
      }, timeoutMs) : null;
      waiter = {
        cmd0: cmd0b, cmd1,
        resolve: (d) => { if (timer) clearTimeout(timer); resolve(d); },
      };
      this._areqWaiters.push(waiter);
    });
  }

  // ── AF data send ────────────────────────────────────────────────────────────

  /**
   * Send a ZCL/AF data frame to a short-addressed device.
   * dstAddr  : numeric network address (e.g. 0x1234)
   * dstEP    : destination endpoint
   * clusterID: cluster ID (number)
   * payload  : Uint8Array or Array of bytes
   */
  async _sendAfData(dstAddr, dstEP, clusterID, payload, srcEP = 1, timeoutMs = 300) {
    const data = [
      dstAddr & 0xFF, (dstAddr >> 8) & 0xFF,  // DstAddr LE
      dstEP,                                    // DestEP
      srcEP,                                    // SrcEP
      clusterID & 0xFF, (clusterID >> 8) & 0xFF,// ClusterID LE
      0x01,                                     // TransID
      0x00,                                     // TxOptions
      0x0F,                                     // Radius
      payload.length,                           // Len
      ...payload,
    ];
    const rsp = await this._sendSreq(CMD.AF_DATA_REQUEST.c0, CMD.AF_DATA_REQUEST.c1, data, timeoutMs);
    if (rsp.data[0] !== 0) throw new Error(`AF_DATA_REQUEST failed status=0x${rsp.data[0].toString(16)}`);
  }

  /**
   * Write Poll Control cluster attributes to minimise the device's radio
   * wake-up interval, reducing button-press latency.
   *
   * Poll Control cluster 0x0020, ZCL Write Attributes command (0x02):
   *  - Long  Poll Interval (0x0001, uint32): 4 quarter-seconds = 1 s
   *  - Short Poll Interval (0x0002, uint16): 2 quarter-seconds = 0.5 s
   */
  async _configureDevice(nwkAddrNum, ep = 1) {
    if (this._configuredDevices.has(nwkAddrNum)) return;
    // Mark immediately — prevents concurrent calls (TC_DEV_IND + ANNCE_IND fire together)
    // and prevents retries if the OTA write fails or times out.
    this._configuredDevices.add(nwkAddrNum);
    this._log('info', `Configuring Poll Control for 0x${nwkAddrNum.toString(16).padStart(4,'0')}…`);
    // ZCL frame: frame-control=0x00, seq=0x01, cmd=0x02 (Write Attributes)
    const zclHeader = [0x00, 0x01, 0x02];

    // Long Poll Interval attr 0x0001, type uint32 (0x23), value 4 (LE 4 bytes)
    const longPoll  = [0x01, 0x00, 0x23, 0x04, 0x00, 0x00, 0x00];
    // Short Poll Interval attr 0x0002, type uint16 (0x21), value 2 (LE 2 bytes)
    const shortPoll = [0x02, 0x00, 0x21, 0x02, 0x00];

    const payload = [...zclHeader, ...longPoll, ...shortPoll];
    try {
      // 500 ms timeout: AF_DATA_REQUEST SRSP should come back from the coordinator
      // almost instantly (Z-Stack just queues the frame). If it doesn't, the device
      // likely doesn't support Poll Control — fail fast rather than blocking 3 seconds.
      await this._sendAfData(nwkAddrNum, ep, 0x0020, payload, 1, 200);
      this._log('ok', `Poll Control configured for 0x${nwkAddrNum.toString(16).padStart(4,'0')}`);
    } catch (e) {
      this._log('warn', `Poll Control config failed (device may not support it): ${e.message}`);
    }
  }

  // ── Frame dispatcher ────────────────────────────────────────────────────────

  _handleFrame(cmd0b, cmd1, data) {
    const type = (cmd0b >> 5) & 0x07;

    if (type === MT_TYPE.SRSP) {
      if (this._pendingSrsp?.cmd0 === cmd0b && this._pendingSrsp?.cmd1 === cmd1) {
        this._pendingSrsp.resolve({ cmd0: cmd0b, cmd1, data });
      } else {
        this._log('warn', `Unexpected SRSP 0x${cmd0b.toString(16).padStart(2,'0')} 0x${cmd1.toString(16).padStart(2,'0')}`);
      }
      return;
    }

    if (type === MT_TYPE.AREQ) {
      // Wake AREQ waiters
      const idx = this._areqWaiters.findIndex(w => w.cmd0 === cmd0b && w.cmd1 === cmd1);
      if (idx !== -1) this._areqWaiters.splice(idx, 1)[0].resolve(data);
      this._handleAreq(cmd0b, cmd1, data);
    }
  }

  _handleAreq(cmd0b, cmd1, data) {
      const key  = (cmd0b << 8) | cmd1;
    const k    = (c) => (c.c0 << 8) | c.c1;

    switch (key) {

      case k(CMD.ZDO_STATE_IND): {
        const state = data[0];
        const name  = ZDO_STATE_NAMES[state] ?? `unknown (${state})`;
        this._log('info', `ZDO state: ${state} — ${name}`);
        this._emit('state-change', { state, name });
        break;
      }

      case k(CMD.ZDO_TC_DEV_IND): {
        const nwkAddr = '0x' + le16(data, 0).toString(16).toUpperCase().padStart(4, '0');
        const ieee    = ieeeToStr(data, 2);
        const action  = data[10]; // 0=join, 1=leave
        if (action === 0) {
          this._log('join', `Device joined  IEEE: ${ieee}  NWK: ${nwkAddr}`);
          this._registerDevice(ieee, le16(data, 0));
          this._emit('device-joined', { ieee, nwkAddr, via: 'TC_DEV_IND' });
          this._configureDevice(le16(data, 0)).catch(() => {});
        } else {
          this._log('warn', `Device left (TC)  IEEE: ${ieee}`);
          this._joinedDevices.delete(le16(data, 0));
          this._emit('device-left', { ieee });
        }
        break;
      }

      case k(CMD.ZDO_ANNCE_IND): {
        const nwkAddrNum   = le16(data, 2);
        const nwkAddr      = '0x' + nwkAddrNum.toString(16).toUpperCase().padStart(4, '0');
        const ieee         = ieeeToStr(data, 4);
        const capabilities = data[12];
        this._log('join', `Device announcement  IEEE: ${ieee}  NWK: ${nwkAddr}  Caps: 0x${capabilities.toString(16).padStart(2,'0')}`);
        this._registerDevice(ieee, nwkAddrNum);
        this._emit('device-joined', { ieee, nwkAddr, capabilities, via: 'ANNCE' });
        // Only configure if TC_DEV_IND was never seen for this device.
        // ZDO_ANNCE_IND fires on every button press (end devices re-announce on wake-up),
        // so calling _configureDevice here unconditionally caused a 3-second SREQ timeout
        // on every button change.
        if (!this._configuredDevices.has(nwkAddrNum)) {
          this._configureDevice(nwkAddrNum).catch(() => {});
        }
        break;
      }

      case k(CMD.ZDO_LEAVE_IND): {
        // ZDO_LEAVE_IND layout:
        //   data[0..1] : SrcAddr (NWK)
        //   data[2..9] : ExtAddr (IEEE)
        //   data[10]   : Request (1 = device requested leave)
        //   data[11]   : Remove  (1 = remove children)
        //   data[12]   : Rejoin  (1 = device intends to rejoin)
        const nwkAddrNum = le16(data, 0);
        const ieee       = ieeeToStr(data, 2);
        const rejoin     = data[12] === 1;
        this._log('warn', `Device leaving  IEEE: ${ieee}  Rejoin: ${rejoin}`);
        this._emit('device-left', { ieee });
        // Always clear from both tracking sets so state is reset after rejoin.
        this._configuredDevices.delete(nwkAddrNum);
        this._joinedDevices.delete(nwkAddrNum);
        // If the device is doing a leave-with-rejoin, briefly open permit join so
        // Z-Stack accepts the incoming rejoin request.
        if (rejoin) {
          this._log('info', 'Leave-with-rejoin detected — opening permit join for 10 s…');
          this._sendPermitJoinCmd(10).catch(() => {});
        }
        break;
      }

      case k(CMD.ZDO_PJ_IND): {
        const dur = data[0];
        this._log('info', `Permit join indication: ${dur === 0 ? 'closed' : `${dur} s`}`);
        break;
      }

      case k(CMD.AF_INCOMING_MSG): {
        const groupID    = le16(data, 0);
        const clusterID  = le16(data, 2);
        const srcAddrNum = le16(data, 4);
        const srcAddr    = '0x' + srcAddrNum.toString(16).toUpperCase().padStart(4, '0');
        const srcEP      = data[6];
        const dstEP      = data[7];
        const lqi        = data[9];
        const msgLen     = data[16];
        // ZCL payload starts at byte 17 (byte 16 is the length field itself)
        const rawPayload = Array.from(data.slice(17, 17 + msgLen));
        const payload    = rawPayload.map(b => b.toString(16).padStart(2, '0')).join(' ');
        const detail = {
          srcAddr,
          srcEP,
          dstEP,
          groupID:   `0x${groupID.toString(16).padStart(4, '0')}`,
          clusterID: `0x${clusterID.toString(16).padStart(4, '0')}`,
          lqi,
          msgLen,
          payload,
        };
        this._log('msg',
          `AF message from ${srcAddr} EP:${srcEP}→${dstEP} cluster:0x${clusterID.toString(16).padStart(4,'0')} LQI:${lqi}`,
          detail,
        );
        if (!this._joinedDevices.has(srcAddrNum)) {
          this._log('warn', `Ignoring message from ${srcAddr} — device not joined`);
          this._emit('device-unknown', { srcAddr });
          break;
        }
        this._emit('message', detail);

        // Send ZCL Default Response so the device releases its radio immediately.
        // Without this, end devices (especially Tuya) hold the radio open waiting
        // for the ack, blocking further messages for ~3 seconds.
        if (msgLen >= 3) {
          const zclFrameCtrl         = rawPayload[0];
          const disableDefaultResp   = (zclFrameCtrl >> 4) & 0x01;
          if (!disableDefaultResp) {
            const manufacturerSpecific = (zclFrameCtrl >> 2) & 0x01;
            const zclSeq = manufacturerSpecific ? rawPayload[3] : rawPayload[1];
            const zclCmd = manufacturerSpecific ? rawPayload[4] : rawPayload[2];
            // 0x18 = global frame, server→client, disable-default-response
            const rspFrame = [0x18, zclSeq, 0x0B, zclCmd, 0x00];
            this._sendAfData(srcAddrNum, srcEP, clusterID, rspFrame, dstEP, 500).catch(() => {});
          }
        }
        break;
      }

      case k(CMD.SYS_RESET_IND): {
        this._log('warn', `SYS_RESET_IND — reason: ${RESET_REASONS[data[0]] ?? data[0]}`);
        break;
      }

      default:
        this._log('info',
          `AREQ 0x${cmd0b.toString(16).padStart(2,'0')} 0x${cmd1.toString(16).padStart(2,'0')}` +
          ` [${Array.from(data).map(b => b.toString(16).padStart(2,'0')).join(' ')}]`,
        );
    }
  }

  // ── Bootloader skip ─────────────────────────────────────────────────────────

  async _bootloaderSkip() {
    // Quick ping first — if firmware already running we're done
    this._log('info', 'Quick SYS_PING check…');
    const alreadyRunning = await this._sendSreq(CMD.SYS_PING.c0, CMD.SYS_PING.c1, [], 500)
      .then(() => true).catch(() => false);
    if (alreadyRunning) {
      this._log('ok', 'Firmware already running — bootloader skip not needed');
      return;
    }
    // Send BSL escape byte (0xEF) to jump to application firmware
    this._log('info', 'No response — sending bootloader escape byte 0xEF…');
    await this._sendRaw(new Uint8Array([0xEF]));
    await delay(1000);
  }

  // ── Coordinator initialisation ──────────────────────────────────────────────

  async _initCoordinator() {
    // SYS_PING — retry up to 5 times; bootloader skip already handled any slow startup
    const MAX_PING = 5;
    let ping;
    for (let attempt = 1; attempt <= MAX_PING; attempt++) {
      try {
        this._log('info', `SYS_PING attempt ${attempt}/${MAX_PING}…`);
        ping = await this._sendSreq(CMD.SYS_PING.c0, CMD.SYS_PING.c1);
        break;
      } catch (e) {
        if (attempt === MAX_PING) throw new Error(`SYS_PING failed after ${MAX_PING} attempts: ${e.message}`);
        this._log('warn', `SYS_PING attempt ${attempt} failed — retrying in 1 s…`);
        await delay(1000);
      }
    }
    this._log('ok', `SYS_PING OK — capabilities 0x${le16(ping.data, 0).toString(16).padStart(4, '0')}`);

    // Firmware version
    try {
      const ver = await this._sendSreq(CMD.SYS_VERSION.c0, CMD.SYS_VERSION.c1);
      const fw  = `${ver.data[2]}.${ver.data[3]}.${ver.data[4]}`;
      this._log('info', `Firmware version: ${fw}`);
      this._emit('firmware-version', { fw });
    } catch (e) {
      this._log('warn', `Could not read firmware version: ${e.message}`);
    }

    // Device info (IEEE + short addr)
    try {
      const dev   = await this._sendSreq(CMD.UTIL_GET_DEVICE.c0, CMD.UTIL_GET_DEVICE.c1);
      const ieee  = ieeeToStr(dev.data, 0);
      const short = '0x' + le16(dev.data, 8).toString(16).toUpperCase().padStart(4, '0');
      this._log('info', `Coordinator IEEE: ${ieee}  Short: ${short}`);
      this._emit('coordinator-info', { ieee, short });
    } catch (e) {
      this._log('warn', `Could not read device info: ${e.message}`);
    }

    // Start the network
    this._log('info', 'Starting Zigbee network (ZDO_STARTUP_FROM_APP)…');
    this._emit('coordinator-status', { status: 'starting' });

    const startup = await this._sendSreq(CMD.ZDO_STARTUP.c0, CMD.ZDO_STARTUP.c1, [0x64, 0x00]);
    const startMap = { 0: 'Restored from NV', 1: 'New network', 2: 'Not started' };
    const startStatus = startMap[startup.data[0]] ?? `Unknown (${startup.data[0]})`;
    this._log('info', `ZDO_STARTUP → ${startStatus}`);

    if (startup.data[0] === 2) {
      this._log('error', 'Coordinator failed to start. Check the dongle.');
      this._emit('coordinator-status', { status: 'failed' });
      return false;
    }

    // Wait for state=9 (ZB_COORD).
    // If the coordinator was already running (startup status=0, "Restored from NV"),
    // Z-Stack won't emit a new ZDO_STATE_IND because the state didn't change.
    // In that case we race against a short timeout and assume ready.
    const alreadyRunning = startup.data[0] === 0;
    this._log('info', alreadyRunning
      ? 'Coordinator already running — waiting briefly for ZDO_STATE_IND or assuming ready…'
      : 'Waiting for coordinator to become ready…');
    await new Promise((resolve) => {
      let settled = false;
      const done = () => { if (!settled) { settled = true; resolve(); } };

      const check = (data) => {
        const state = data[0];
        this._emit('state-change', { state, name: ZDO_STATE_NAMES[state] ?? `unknown (${state})` });
        if (state === 9) {
          done();
        } else {
          this._areqWaiters.push({ cmd0: CMD.ZDO_STATE_IND.c0, cmd1: CMD.ZDO_STATE_IND.c1, resolve: check });
        }
      };
      this._areqWaiters.push({ cmd0: CMD.ZDO_STATE_IND.c0, cmd1: CMD.ZDO_STATE_IND.c1, resolve: check });

      // If already running, Z-Stack won't re-emit ZDO_STATE_IND — resolve after timeout
      if (alreadyRunning) {
        setTimeout(() => {
          // Remove our waiter if it hasn't fired yet
          const idx = this._areqWaiters.findIndex(w => w.resolve === check);
          if (idx !== -1) this._areqWaiters.splice(idx, 1);
          this._log('info', 'No ZDO_STATE_IND received — coordinator already at state 9');
          done();
        }, 2000);
      }
    });

    // Register AF endpoints matching zigbee-herdsman exactly so Z-Stack delivers
    // AF_INCOMING_MSG regardless of which profile/endpoint the remote uses.
    // Each entry: [ep, profileID, deviceID, inClusters[], outClusters[]]
    this._log('info', 'Registering AF endpoints…');
    const EP_DEFS = [
      [  1, 0x0104, 0x0005, [], []],
      [  2, 0x0101, 0x0005, [], []],
      [  3, 0x0104, 0x0005, [], []],
      [  4, 0x0107, 0x0005, [], []],
      [  5, 0x0108, 0x0005, [], []],
      [  6, 0x0109, 0x0005, [], []],
      [  8, 0x0104, 0x0005, [], []],
      [ 10, 0x0104, 0x0005, [], []],
      [ 11, 0x0104, 0x0400, [0x0501, 0x000A], [0x0500, 0x0502]],  // IAS Zone + OTA
      [110, 0x0104, 0x0005, [], []],
      [ 12, 0xC05E, 0x0005, [], []],  // ZLL
      [ 13, 0x0104, 0x0005, [0x0019], []],  // OTA Upgrade cluster
      [ 47, 0x0104, 0x0005, [], []],
      [239, 0xC001, 0x0005, [], []],
      [242, 0xA1E0, 0x0005, [], []],  // Green Power
    ];
    for (const [ep, profId, devId, inClusters, outClusters] of EP_DEFS) {
      // Build cluster lists as LE uint16 pairs
      const inList  = inClusters.flatMap(c  => [c  & 0xFF, (c  >> 8) & 0xFF]);
      const outList = outClusters.flatMap(c => [c  & 0xFF, (c  >> 8) & 0xFF]);
      const afReg = await this._sendSreq(CMD.AF_REGISTER.c0, CMD.AF_REGISTER.c1, [
        ep,
        profId & 0xFF, (profId >> 8) & 0xFF,
        devId  & 0xFF, (devId  >> 8) & 0xFF,
        0x00,                   // deviceVersion
        0x00,                   // latencyReq
        inClusters.length, ...inList,
        outClusters.length, ...outList,
      ]);
      if (afReg.data[0] === 0) this._log('ok', `AF endpoint ${ep} registered (profile 0x${profId.toString(16).padStart(4,'0')})`);
      else this._log('warn', `AF_REGISTER EP${ep} status: 0x${afReg.data[0].toString(16)}`);
    }

    // Open permit join at startup so previously-paired devices can rejoin automatically.
    this._log('info', 'Opening permit join for 254 s at startup…');
    await this.permitJoin(true, 254).catch(e => this._log('warn', `Startup permit join failed: ${e.message}`));

    // Read devices already registered in the coordinator and reconcile with local storage.
    await this.readNetworkDevices().catch(e => this._log('warn', `readNetworkDevices failed: ${e.message}`));

    this._emit('coordinator-status', { status: 'ready' });
  }

  // ── Permit-join internals ───────────────────────────────────────────────────

  async _sendPermitJoinCmd(duration) {
    const r = await this._sendSreq(CMD.ZDO_PERMIT_JOIN.c0, CMD.ZDO_PERMIT_JOIN.c1,
      [0x0F, 0xFC, 0xFF, duration & 0xFF, 0x01], // broadcast to 0xFFFC
    );
    const ok = r.data[0] === 0;
    if (ok) this._log('ok', `Permit join ${duration === 0 ? 'disabled' : `enabled for ${duration} s`}`);
    else    this._log('warn', `PERMIT_JOIN status: 0x${r.data[0].toString(16)}`);
  }

  _startPjCountdown() {
    this._stopPjCountdown();
    this._emitPjStatus();
    this._pjTimerId = setInterval(async () => {
      this._pjRemaining -= 1;
      this._emitPjStatus();
      if (this._pjRemaining <= 0) { this._stopPjCountdown(); return; }
      // Re-schedule every 254 s for long durations
      if ((this._pjTotal - this._pjRemaining) % 254 === 0) {
        await this._sendPermitJoinCmd(Math.min(this._pjRemaining, 254)).catch(e => this._log('error', e.message));
      }
    }, 1000);
  }

  _stopPjCountdown() {
    if (this._pjTimerId) { clearInterval(this._pjTimerId); this._pjTimerId = null; }
    this._pjRemaining = 0;
    this._emitPjStatus();
  }

  _emitPjStatus() {
    this._emit('permit-join-status', {
      active:    this._pjRemaining > 0,
      remaining: this._pjRemaining,
      total:     this._pjTotal,
    });
  }

  // ── Event helpers ───────────────────────────────────────────────────────────

  _emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  _log(level, message, detail = null) {
    this._emit('log', { level, message, detail });
  }
}
